// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Subject,
  take,
  catchError,
  of,
  finalize,
  BehaviorSubject,
  Subscription,
} from 'rxjs';
import {
  PortalWebAppConfiguration,
  UsefulAppConfigurationModel,
} from '../../../api/remote-api/.portal.generated/models';
import { defaultPortalWebAppConfiguration } from '../../../config/default-portal-web-app-configuration';

import { environment } from 'src/environments/environment';
import { getConfiguration } from '../../../api/remote-api/.portal.generated/fn/system-overview/get-configuration';
import {
  getUsefulAppConfiguration,
  GetUsefulAppConfiguration$Params,
} from '../../../api/remote-api/.portal.generated/fn/system-overview/get-useful-app-configuration';
@Injectable()
export class PortalWebAppConfigurationService {
  private portalWebAppConfiguration: PortalWebAppConfiguration & {
    // TODO: missing in API SPEC!
    mrqDashboardGeneric: string;
    termsOfUseChinaPath: string;
    // temporary killswitch:
    disableSRSTaskButtonCT?: boolean;
    disableSRSTaskButtonMR?: boolean;
  } = defaultPortalWebAppConfiguration;

  #loaded$ = new Subject<boolean>();
  loaded$ = this.#loaded$.asObservable();

  private readonly usefulAppConfigurationSubject =
    new BehaviorSubject<UsefulAppConfigurationModel>(
      {} as UsefulAppConfigurationModel,
    );
  usefulAppConfiguration$ = this.usefulAppConfigurationSubject.asObservable();

  private readonly isAppConfigurationLoadingSubject =
    new BehaviorSubject<boolean>(true);
  isAppConfigurationLoading$ =
    this.isAppConfigurationLoadingSubject.asObservable();
  private usefulAppConfigurationRequestSubscription?: Subscription;

  // cannot use SystemOverviewService here because of circular dependency
  constructor(private httpClient: HttpClient) {
    this.loadPortalWebAppConfiguration();
  }

  public getPortalWebAppConfiguration(): typeof this.portalWebAppConfiguration {
    return this.portalWebAppConfiguration;
  }

  public getTermsOfUseChinaApiPath(): string {
    return this.getUserDocumentationApiPath(
      this.portalWebAppConfiguration.termsOfUseChinaPath,
    );
  }

  private getUserDocumentationApiPath(relativePath: string): string {
    const bytes = new TextEncoder().encode(relativePath);
    let binary = '';
    for (const value of bytes) {
      binary += String.fromCodePoint(value);
    }

    return `/portal/api/filestorage/userdocumentation/${btoa(binary)}`;
  }

  private loadPortalWebAppConfiguration() {
    const root = environment.apiRootOverride || getConfiguration.ROOTURL;
    // todo handle error scenario
    this.httpClient
      .get(root + getConfiguration.PATH, {
        responseType: 'json',
      })
      .pipe(take(1))
      .subscribe((json) => {
        const configuration = json as unknown as PortalWebAppConfiguration;
        if (!configuration) {
          return;
        }

        // remove null or undefined entries
        for (const v of Object.keys(configuration)) {
          if (
            configuration[v as keyof PortalWebAppConfiguration] === null ||
            configuration[v as keyof PortalWebAppConfiguration] === undefined
          ) {
            delete configuration[v as keyof PortalWebAppConfiguration];
          }
        }

        // merge (only at root level, no deep merge)
        this.portalWebAppConfiguration = {
          ...defaultPortalWebAppConfiguration,
          ...configuration,
        };
        this.#loaded$.next(true);
      });
  }

  public getUsefulAppConfiguration(
    request: GetUsefulAppConfiguration$Params,
  ): void {
    this.usefulAppConfigurationRequestSubscription?.unsubscribe();
    this.isAppConfigurationLoadingSubject.next(true);
    this.usefulAppConfigurationRequestSubscription = this.httpClient
      .get(getUsefulAppConfiguration.ROOTURL + getUsefulAppConfiguration.PATH, {
        params: { ...request } as {
          [param: string]:
            | string
            | number
            | boolean
            | readonly (string | number | boolean)[];
        },
      })
      .pipe(
        take(1),
        catchError((err) => {
          console.log('Fetching useful app configuration failed', err);
          return of({} as UsefulAppConfigurationModel);
        }),
        finalize(() => {
          this.isAppConfigurationLoadingSubject.next(false);
          this.usefulAppConfigurationRequestSubscription = undefined;
        }),
      )
      .subscribe((config) => {
        this.usefulAppConfigurationSubject.next(
          config as UsefulAppConfigurationModel,
        );
      });
  }
}
